<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="modelValue" class="modal-overlay" @click="closeOnBackdrop && close()">
        <div 
          class="modal-content" 
          :class="[
            `modal-${size}`,
            variant ? `modal-${variant}` : ''
          ]" 
          @click.stop
        >
          <!-- Custom Header Slot oder Standard Header -->
          <div v-if="$slots.header" class="modal-header">
            <slot name="header"></slot>
          </div>
          <div v-else class="modal-header">
            <h3>
              <i v-if="icon" :class="icon" class="modal-icon"></i>
              {{ title }}
            </h3>
            <button v-if="closable" class="modal-close" @click="close()" type="button">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Body Content -->
          <div class="modal-body">
            <slot></slot>
          </div>

          <!-- Custom Footer Slot oder Standard Footer -->
          <div v-if="$slots.footer || showFooter" class="modal-footer">
            <slot name="footer">
              <button 
                v-if="showCancel"
                class="btn btn-secondary" 
                type="button"
                :disabled="busy"
                @click="cancel()"
              >
                <i class="fas fa-times"></i> {{ cancelLabel }}
              </button>
              <button 
                v-if="showConfirm"
                class="btn" 
                :class="`btn-${confirmVariant}`"
                type="button"
                :disabled="confirmDisabled || busy"
                @click="confirm()"
              >
                <i :class="confirmIcon"></i> {{ confirmLabel }}
                <span v-if="busy" class="btn-spinner"></span>
              </button>
            </slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md',
    validator: (value: string) => ['sm', 'md', 'lg', 'xl', 'xxl'].includes(value)
  },
  variant: {
    type: String,
    default: '',
    validator: (value: string) => ['', 'danger', 'warning', 'info'].includes(value)
  },
  closable: {
    type: Boolean,
    default: true
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true
  },
  showFooter: {
    type: Boolean,
    default: false
  },
  showCancel: {
    type: Boolean,
    default: true
  },
  showConfirm: {
    type: Boolean,
    default: true
  },
  cancelLabel: {
    type: String,
    default: 'Abbrechen'
  },
  confirmLabel: {
    type: String,
    default: 'Bestätigen'
  },
  confirmVariant: {
    type: String,
    default: 'primary',
    validator: (value: string) => ['primary', 'danger', 'warning', 'success'].includes(value)
  },
  confirmIcon: {
    type: String,
    default: 'fas fa-check'
  },
  confirmDisabled: {
    type: Boolean,
    default: false
  },
  busy: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'close'])

const close = () => {
  emit('update:modelValue', false)
  emit('close')
}

const cancel = () => {
  emit('cancel')
  close()
}

const confirm = () => {
  emit('confirm')
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
}

.modal-content {
  background: var(--color-surface, #ffffff);
  color: var(--color-text, #0f172a);
  border-radius: var(--border-radius-md, 0.375rem);
  box-shadow: var(--box-shadow-lg, 0 30px 60px rgba(15, 23, 42, 0.2));
  border: 1px solid var(--color-border, rgba(15, 23, 42, 0.08));
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Size Variants */
.modal-sm {
  max-width: 400px;
}

.modal-md {
  max-width: 520px;
}

.modal-lg {
  max-width: 800px;
}

.modal-xl {
  max-width: 1200px;
}

.modal-xxl {
  max-width: 75vw;
}

/* Color Variants */
.modal-danger {
  --modal-accent: #dc2626;
  --modal-accent-soft: rgba(220, 38, 38, 0.1);
}

.modal-warning {
  --modal-accent: #d97706;
  --modal-accent-soft: rgba(217, 119, 6, 0.12);
}

.modal-info {
  --modal-accent: #2563eb;
  --modal-accent-soft: rgba(37, 99, 235, 0.12);
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-lg, 1.5rem);
  border-bottom: 1px solid var(--color-border);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-icon {
  color: var(--modal-accent, var(--color-primary));
  font-size: 1.1rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: var(--spacing-xs, 0.5rem);
  border-radius: 50%;
  transition: all 0.2s;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--color-background-muted);
  color: var(--color-text);
}

/* Body */
.modal-body {
  padding: var(--spacing-lg, 1.5rem);
  overflow-y: auto;
  flex: 1;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm, 0.75rem);
  padding: var(--spacing-lg, 1.5rem);
  border-top: 1px solid var(--color-border);
}

/* Button Spinner */
.btn-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-left: 0.5rem;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.97);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 1rem;
  }
  
  .modal-content {
    max-height: 95vh;
  }
}
</style>
